import React, { useState, useEffect } from 'react';
import { Accordion, Card, Table, Row, Col, Badge } from 'react-bootstrap';
import {
    FiCalendar, FiTruck, FiDroplet, FiSettings,
    FiMapPin, FiTag, FiActivity, FiAlertCircle
} from 'react-icons/fi';

/**
 * Araç detay sayfasında kullanılan akordiyon bileşeni
 * Araç bilgileri, teknik özellikler ve hasar bilgilerini görüntüler
 * 
 * @param {Object} props.vehicle - Araç detay verisi
 */
const VehicleDetailsAccordion = ({ vehicle }) => {
    // Varsayılan olarak ilk panel açık
    const [activeKey, setActiveKey] = useState('0');

    // İlk paneli başlangıçta açık hale getir
    useEffect(() => {
        setActiveKey('0');
    }, []);

    if (!vehicle) return null;

    // Temel araç bilgileri
    const vehicleInfoItems = [
        { label: 'Satıcı', value: vehicle.owner?.username || 'Otoprime' },
        { label: 'İlan No', value: vehicle._id || '' },
        { label: 'İlan Tarihi', value: new Date(vehicle.createdAt).toLocaleDateString('tr-TR') },
        { label: 'Kasa Tipi', value: vehicle.bodyType || 'Belirtilmemiş' },
        { label: 'Marka', value: vehicle.brand || 'Belirtilmemiş' },
        { label: 'Model', value: vehicle.model || 'Belirtilmemiş' },
        { label: 'Yıl', value: vehicle.year || 'Belirtilmemiş' },
        { label: 'Yakıt', value: vehicle.fuelType || 'Belirtilmemiş' },
        { label: 'Vites', value: vehicle.transmission || 'Belirtilmemiş' },
        { label: 'KM', value: vehicle.mileage ? `${vehicle.mileage?.toLocaleString()} km` : 'Belirtilmemiş' },
        { label: 'Motor Gücü', value: vehicle.generalFeatures?.enginePower || 'Belirtilmemiş' },
        { label: 'Motor Hacmi', value: vehicle.engineDetails?.volume || 'Belirtilmemiş' },
        { label: 'Çekiş Tipi', value: vehicle.driveTrain || '4x2' },
        { label: 'Renk', value: vehicle.color || 'Belirtilmemiş' },
        { label: 'Garanti', value: vehicle.warranty ? 'Var' : 'Yok' },
        { label: 'Son Güncelleme', value: new Date(vehicle.updatedAt).toLocaleDateString('tr-TR') },
    ];

    // Duruma göre stil sınıfı belirle
    const getConditionClass = (condition) => {
        switch (condition) {
            case 'Orijinal': return 'condition-original';
            case 'Boyalı': return 'condition-painted';
            case 'Değişen': return 'condition-changed';
            case 'Lokal': return 'condition-local';
            default: return '';
        }
    };

    return (
        <Accordion defaultActiveKey="0" activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
            {/* Araç Bilgileri Paneli */}
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <h5 className="mb-0">Araç Bilgileri</h5>
                </Accordion.Header>
                <Accordion.Body>
                    <Row>
                        <Col md={6}>
                            <Table bordered hover responsive>
                                <tbody>
                                    {vehicleInfoItems.slice(0, Math.ceil(vehicleInfoItems.length / 2)).map((item, index) => (
                                        <tr key={`info-left-${index}`}>
                                            <td width="40%" className="fw-bold">{item.label}</td>
                                            <td>{item.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Col>
                        <Col md={6}>
                            <Table bordered hover responsive>
                                <tbody>
                                    {vehicleInfoItems.slice(Math.ceil(vehicleInfoItems.length / 2)).map((item, index) => (
                                        <tr key={`info-right-${index}`}>
                                            <td width="40%" className="fw-bold">{item.label}</td>
                                            <td>{item.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </Accordion.Body>
            </Accordion.Item>

            {/* Teknik Özellikler Paneli */}
            <Accordion.Item eventKey="1">
                <Accordion.Header>
                    <h5 className="mb-0">Teknik Özellikler</h5>
                </Accordion.Header>
                <Accordion.Body>
                    {/* Genel Özellikler */}
                    {vehicle.generalFeatures && (
                        <div className="mb-4">
                            <h6 className="mb-3 fw-bold">Genel Özellikler</h6>
                            <Table bordered hover responsive>
                                <tbody>
                                    {vehicle.generalFeatures.acceleration && (
                                        <tr>
                                            <td width="40%" className="fw-bold">Hızlanma 0-100 km/saat</td>
                                            <td>{vehicle.generalFeatures.acceleration}</td>
                                        </tr>
                                    )}
                                    {vehicle.generalFeatures.enginePower && (
                                        <tr>
                                            <td className="fw-bold">Motor Gücü</td>
                                            <td>{vehicle.generalFeatures.enginePower}</td>
                                        </tr>
                                    )}
                                    {vehicle.generalFeatures.maxSpeed && (
                                        <tr>
                                            <td className="fw-bold">Azami Sürat</td>
                                            <td>{vehicle.generalFeatures.maxSpeed}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Motor ve Performans */}
                    {vehicle.engineDetails && (
                        <div className="mb-4">
                            <h6 className="mb-3 fw-bold">Motor ve Performans</h6>
                            <Table bordered hover responsive>
                                <tbody>
                                    {vehicle.engineDetails.power && (
                                        <tr>
                                            <td width="40%" className="fw-bold">Maksimum Güç</td>
                                            <td>{vehicle.engineDetails.power}</td>
                                        </tr>
                                    )}
                                    {vehicle.engineDetails.volume && (
                                        <tr>
                                            <td className="fw-bold">Motor Hacmi</td>
                                            <td>{vehicle.engineDetails.volume}</td>
                                        </tr>
                                    )}
                                    {vehicle.engineDetails.torque && (
                                        <tr>
                                            <td className="fw-bold">Maksimum Tork</td>
                                            <td>{vehicle.engineDetails.torque}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Yakıt Tüketimi */}
                    {vehicle.fuelConsumption && (
                        <div className="mb-4">
                            <h6 className="mb-3 fw-bold">Yakıt Tüketimi</h6>
                            <Table bordered hover responsive>
                                <tbody>
                                    {vehicle.fuelConsumption.tankCapacity && (
                                        <tr>
                                            <td width="40%" className="fw-bold">Yakıt Depo Hacmi</td>
                                            <td>{vehicle.fuelConsumption.tankCapacity}</td>
                                        </tr>
                                    )}
                                    {vehicle.fuelConsumption.cityConsumption && (
                                        <tr>
                                            <td className="fw-bold">Şehir İçi</td>
                                            <td>{vehicle.fuelConsumption.cityConsumption}</td>
                                        </tr>
                                    )}
                                    {vehicle.fuelConsumption.highwayConsumption && (
                                        <tr>
                                            <td className="fw-bold">Şehir Dışı</td>
                                            <td>{vehicle.fuelConsumption.highwayConsumption}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Boyutlar */}
                    {vehicle.dimensions && (
                        <div className="mb-4">
                            <h6 className="mb-3 fw-bold">Boyutlar</h6>
                            <Table bordered hover responsive>
                                <tbody>
                                    {vehicle.dimensions.tireSize && (
                                        <tr>
                                            <td width="40%" className="fw-bold">Lastik Ölçüleri</td>
                                            <td>{vehicle.dimensions.tireSize}</td>
                                        </tr>
                                    )}
                                    {vehicle.dimensions.height && (
                                        <tr>
                                            <td className="fw-bold">Yükseklik</td>
                                            <td>{vehicle.dimensions.height}</td>
                                        </tr>
                                    )}
                                    {vehicle.dimensions.length && (
                                        <tr>
                                            <td className="fw-bold">Uzunluk</td>
                                            <td>{vehicle.dimensions.length}</td>
                                        </tr>
                                    )}
                                    {vehicle.dimensions.luggageCapacity && (
                                        <tr>
                                            <td className="fw-bold">Bagaj Kapasitesi</td>
                                            <td>{vehicle.dimensions.luggageCapacity}</td>
                                        </tr>
                                    )}
                                    {vehicle.dimensions.emptyWeight && (
                                        <tr>
                                            <td className="fw-bold">Yüksüz Ağırlık</td>
                                            <td>{vehicle.dimensions.emptyWeight}</td>
                                        </tr>
                                    )}
                                    {vehicle.dimensions.loadedWeight && (
                                        <tr>
                                            <td className="fw-bold">Yüklü Ağırlık</td>
                                            <td>{vehicle.dimensions.loadedWeight}</td>
                                        </tr>
                                    )}
                                    {vehicle.dimensions.width && (
                                        <tr>
                                            <td className="fw-bold">Genişlik</td>
                                            <td>{vehicle.dimensions.width}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Teknik detaylar yoksa mesaj göster */}
                    {!vehicle.generalFeatures && !vehicle.engineDetails &&
                        !vehicle.fuelConsumption && !vehicle.dimensions && (
                            <div className="alert alert-info">
                                Bu araç için detaylı teknik bilgiler girilmemiş.
                            </div>
                        )}
                </Accordion.Body>
            </Accordion.Item>

            {/* Araç Durumu (Hasar Bilgileri) Paneli */}
            <Accordion.Item eventKey="2">
                <Accordion.Header>
                    <h5 className="mb-0">Hasar Bilgileri</h5>
                </Accordion.Header>
                <Accordion.Body>
                    {vehicle.bodyCondition && Object.values(vehicle.bodyCondition).some(value => value) ? (
                        <Row>
                            {Object.entries(vehicle.bodyCondition).map(([part, condition], index) => {
                                if (!condition) return null;

                                // Parça isimlerini Türkçe'ye çevir
                                const partNames = {
                                    frontBumper: 'Ön Tampon',
                                    rearBumper: 'Arka Tampon',
                                    roof: 'Tavan',
                                    trunkLid: 'Bagaj Kaputu',
                                    engineHood: 'Motor Kaputu',
                                    leftFrontDoor: 'Sol Ön Kapı',
                                    rightFrontDoor: 'Sağ Ön Kapı',
                                    leftRearDoor: 'Sol Arka Kapı',
                                    rightRearDoor: 'Sağ Arka Kapı',
                                    leftFrontFender: 'Sol Ön Çamurluk',
                                    rightFrontFender: 'Sağ Ön Çamurluk',
                                    leftRearFender: 'Sol Arka Çamurluk',
                                    rightRearFender: 'Sağ Arka Çamurluk'
                                };

                                return (
                                    <Col key={index} md={6} className="mb-3">
                                        <div className="d-flex align-items-center">
                                            <div className="fw-bold me-2" style={{ minWidth: '150px' }}>
                                                {partNames[part] || part}
                                            </div>
                                            <Badge className={`ms-2 ${getConditionClass(condition)}`}>
                                                {condition}
                                            </Badge>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <div className="alert alert-info">
                            Bu araç için hasar bilgisi girilmemiş.
                        </div>
                    )}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};

export default VehicleDetailsAccordion;